import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import PageTitle from '../components/PageTitle';
import { useCart } from '../context/CartContext';
import { formatCurrency, formatCurrencyShort } from '../utils/currency';

/* ─── Bank Details ─── */
const BANK = {
  account: '1019321760',
  bank: 'United Bank for Africa (UBA)',
  name: 'Greatwood Hotels Ltd',
  email: '',
};

/* ─── Step 1: Bank Transfer Modal ─── */
function BankModal({ total, onConfirmed, onClose }) {
  const [confirmed, setConfirmed] = useState(false);
  const [payForm, setPayForm] = useState({
    bankName: '',
    accountName: '',
    amount: '',
    description: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setPayForm({ ...payForm, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const e = {};
    if (!payForm.bankName.trim()) e.bankName = 'Please enter your bank name';
    if (!payForm.accountName.trim())
      e.accountName = 'Please enter account name used';
    if (!payForm.amount.trim()) e.amount = 'Please enter the amount paid';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSending(true);
    /* 
      In production, replace this timeout with an actual API/email call.
      e.g. fetch('/api/send-payment-email', { method:'POST', body: JSON.stringify({...payForm, total}) })
      The email body should go to BANK.email
    */
    setTimeout(() => {
      setSending(false);
      setSent(true);
      // notify parent to clear cart + show success after 2s
      setTimeout(() => onConfirmed(payForm), 2000);
    }, 1200);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '36px 36px 32px',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 34,
            height: 34,
            borderRadius: '50%',
            border: 'none',
            background: '#f5f5f5',
            cursor: 'pointer',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i className="fas fa-times"></i>
        </button>

        {!confirmed ? (
          /* ── Step 1: Show bank details ── */
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  background: 'rgba(176,141,87,0.12)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                }}
              >
                <i
                  className="fas fa-building-columns"
                  style={{ fontSize: 26, color: '#b08d57' }}
                ></i>
              </div>
              <h4
                style={{
                  fontFamily: 'var(--gw-font-h,serif)',
                  fontSize: 24,
                  color: '#111',
                  marginBottom: 6,
                }}
              >
                Bank Transfer Payment
              </h4>
              <p style={{ color: '#777', fontSize: 14 }}>
                Please transfer the exact amount to the account below
              </p>
            </div>

            {/* Amount due */}
            <div
              style={{
                background: '#111827',
                borderRadius: 8,
                padding: '16px 20px',
                textAlign: 'center',
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                Total Amount Due
              </div>
              <div
                style={{
                  fontSize: 36,
                  fontWeight: 700,
                  color: '#b08d57',
                  fontFamily: 'var(--gw-font-h,serif)',
                }}
              >
                {formatCurrency(total)}
              </div>
            </div>

            {/* Bank details card */}
            <div
              style={{
                border: '2px solid #b08d57',
                borderRadius: 8,
                overflow: 'hidden',
                marginBottom: 24,
              }}
            >
              <div style={{ background: '#b08d57', padding: '10px 18px' }}>
                <span
                  style={{
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Payment Account Details
                </span>
              </div>
              <div
                style={{
                  padding: '18px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                {[
                  {
                    label: 'Bank Name',
                    value: BANK.bank,
                    icon: 'fas fa-university',
                  },
                  {
                    label: 'Account Number',
                    value: BANK.account,
                    icon: 'fas fa-hashtag',
                    mono: true,
                  },
                  {
                    label: 'Account Name',
                    value: BANK.name,
                    icon: 'fas fa-user',
                  },
                ].map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                    }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        background: 'rgba(176,141,87,0.1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#b08d57',
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    >
                      <i className={row.icon}></i>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          color: '#999',
                          marginBottom: 3,
                        }}
                      >
                        {row.label}
                      </div>
                      <div
                        style={{
                          fontSize: row.mono ? 20 : 15,
                          fontWeight: 700,
                          color: '#111',
                          fontFamily: row.mono ? 'monospace' : undefined,
                          letterSpacing: row.mono ? 2 : undefined,
                        }}
                      >
                        {row.value}
                        {row.mono && (
                          <button
                            title="Copy account number"
                            onClick={() => {
                              navigator.clipboard?.writeText(row.value);
                            }}
                            style={{
                              marginLeft: 10,
                              background: 'none',
                              border: '1px solid #e8e3dc',
                              borderRadius: 3,
                              padding: '2px 8px',
                              fontSize: 11,
                              cursor: 'pointer',
                              color: '#777',
                              verticalAlign: 'middle',
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Important note */}
            <div
              style={{
                background: '#fffbf0',
                border: '1px solid #f0d070',
                borderRadius: 6,
                padding: '12px 16px',
                marginBottom: 22,
                display: 'flex',
                gap: 10,
                fontSize: 13,
              }}
            >
              <i
                className="fas fa-circle-info"
                style={{ color: '#d4a017', marginTop: 2, flexShrink: 0 }}
              ></i>
              <span style={{ color: '#555', lineHeight: 1.6 }}>
                After making the transfer, click{' '}
                <strong>"I Have Made the Payment"</strong> and fill in your
                payment details so we can confirm your booking.
              </span>
            </div>

            <button
              onClick={() => setConfirmed(true)}
              style={{
                width: '100%',
                padding: '14px',
                background: '#b08d57',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: 1,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = '#9a7b47')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = '#b08d57')
              }
            >
              <i className="fas fa-check-circle me-2"></i>I Have Made the
              Payment
            </button>
          </>
        ) : (
          /* ── Step 2: Payment confirmation form ── */
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  background: 'rgba(37,211,102,0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                }}
              >
                <i
                  className="fas fa-receipt"
                  style={{ fontSize: 26, color: '#25d366' }}
                ></i>
              </div>
              <h4
                style={{
                  fontFamily: 'var(--gw-font-h,serif)',
                  fontSize: 22,
                  color: '#111',
                  marginBottom: 6,
                }}
              >
                Confirm Your Payment
              </h4>
              <p style={{ color: '#777', fontSize: 13, lineHeight: 1.6 }}>
                Fill in the details of the account you used to pay so we can
                verify and confirm your booking.
              </p>
            </div>

            {sent ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    background: 'rgba(37,211,102,0.12)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <i
                    className="fas fa-circle-check"
                    style={{ fontSize: 32, color: '#25d366' }}
                  ></i>
                </div>
                <h5
                  style={{
                    fontFamily: 'var(--gw-font-h,serif)',
                    fontSize: 20,
                    color: '#111',
                    marginBottom: 8,
                  }}
                >
                  Payment Details Sent!
                </h5>
                <p style={{ color: '#777', fontSize: 14 }}>
                  We've received your payment details and will confirm your
                  booking shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {[
                  {
                    name: 'bankName',
                    label: 'Bank Name *',
                    placeholder: 'e.g. First Bank, GTBank, Zenith…',
                  },
                  {
                    name: 'accountName',
                    label: 'Account Name Used *',
                    placeholder: 'Name on the account you paid from',
                  },
                  {
                    name: 'amount',
                    label: 'Amount Paid *',
                    placeholder: `e.g. ${formatCurrency(total)}`,
                  },
                  {
                    name: 'description',
                    label: 'Payment Description / Narration (optional)',
                    placeholder: 'e.g. GW booking reference, transaction note…',
                  },
                ].map((field) => (
                  <div key={field.name} style={{ marginBottom: 14 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: 'uppercase',
                        color: '#555',
                        marginBottom: 6,
                      }}
                    >
                      {field.label}
                    </label>
                    {field.name === 'description' ? (
                      <textarea
                        name={field.name}
                        value={payForm[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 13px',
                          border: `1.5px solid ${errors[field.name] ? '#dc3545' : '#e8e3dc'}`,
                          borderRadius: 4,
                          fontSize: 14,
                          resize: 'vertical',
                          outline: 'none',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <input
                        name={field.name}
                        value={payForm[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        style={{
                          width: '100%',
                          padding: '10px 13px',
                          border: `1.5px solid ${errors[field.name] ? '#dc3545' : '#e8e3dc'}`,
                          borderRadius: 4,
                          fontSize: 14,
                          outline: 'none',
                          fontFamily: 'inherit',
                        }}
                      />
                    )}
                    {errors[field.name] && (
                      <span
                        style={{
                          fontSize: 12,
                          color: '#dc3545',
                          marginTop: 3,
                          display: 'block',
                        }}
                      >
                        {errors[field.name]}
                      </span>
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={sending}
                  style={{
                    width: '100%',
                    padding: '13px',
                    background: sending ? '#ccc' : '#111827',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 5,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 1,
                    cursor: sending ? 'not-allowed' : 'pointer',
                    marginTop: 6,
                  }}
                >
                  {sending ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>Sending to
                      Hotel…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>Submit Payment
                      Details
                    </>
                  )}
                </button>
                <p
                  style={{
                    fontSize: 11,
                    color: '#aaa',
                    textAlign: 'center',
                    marginTop: 10,
                  }}
                >
                  Your details will be sent to {BANK.email} for verification.
                </p>
              </form>
            )}

            <button
              onClick={() => setConfirmed(false)}
              style={{
                display: 'block',
                margin: '12px auto 0',
                background: 'none',
                border: 'none',
                color: '#999',
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              ← Back to bank details
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Checkout Page ─── */
export default function ShopCheckout() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const tax = cartTotal * 0.08;
  const grandTotal = cartTotal + tax;

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [payDetails, setPayDetails] = useState(null);

  const handle = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Required';
    return e;
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setShowModal(true);
  };

  const handlePaymentConfirmed = (details) => {
    setPayDetails(details);
    setShowModal(false);
    clearCart();
    setPlaced(true);
  };

  /* Success screen */
  if (placed)
    return (
      <div className="page-wrapper">
        <Preloader />
        <Header />
        <div
          style={{
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 520 }}>
            <div
              style={{
                width: 80,
                height: 80,
                background: 'rgba(37,211,102,0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <i
                className="fas fa-circle-check"
                style={{ fontSize: 40, color: '#25d366' }}
              ></i>
            </div>
            <h2
              style={{
                fontFamily: 'var(--gw-font-h,serif)',
                fontSize: 32,
                color: '#111',
                marginBottom: 14,
              }}
            >
              Booking Confirmed!
            </h2>
            <p
              style={{
                color: '#555',
                fontSize: 15,
                lineHeight: 1.8,
                marginBottom: 8,
              }}
            >
              Thank you,{' '}
              <strong>
                {form.firstName} {form.lastName}
              </strong>
              . Your payment details have been received by{' '}
              <strong>Greatwood Hotel</strong>.
            </p>
            <p
              style={{
                color: '#777',
                fontSize: 14,
                lineHeight: 1.8,
                marginBottom: 24,
              }}
            >
              Our team will verify your payment and send a confirmation to{' '}
              <strong>{form.email}</strong> within a few hours. For enquiries
              call{' '}
              <a
                href="tel:+2348076988183"
                style={{ color: '#b08d57', fontWeight: 600 }}
              >
                0807 698 8183
              </a>
              .
            </p>
            {payDetails && (
              <div
                style={{
                  background: '#f9f8f6',
                  border: '1px solid #e8e3dc',
                  borderRadius: 8,
                  padding: '18px 20px',
                  marginBottom: 24,
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 12,
                  }}
                >
                  Payment Summary
                </div>
                {[
                  ['Bank Used', payDetails.bankName],
                  ['Account Name', payDetails.accountName],
                  ['Amount Paid', payDetails.amount],
                  ['Description', payDetails.description || '—'],
                ].map(([k, v], i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 14,
                      marginBottom: 6,
                      color: '#555',
                    }}
                  >
                    <span style={{ color: '#999' }}>{k}</span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: '#111',
                        textAlign: 'right',
                        maxWidth: '60%',
                      }}
                    >
                      {v}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Link to="/rooms" className="h2-btn-primary">
                Browse More Rooms
              </Link>
              <Link to="/" className="h2-btn-ghost">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    );

  /* Empty cart */
  if (cartItems.length === 0)
    return (
      <div className="page-wrapper">
        <Preloader />
        <Header />
        <PageTitle title="Checkout" breadcrumbs={[{ label: 'Checkout' }]} />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <i
            className="fas fa-cart-shopping"
            style={{
              fontSize: 64,
              color: '#ddd',
              display: 'block',
              marginBottom: 20,
            }}
          ></i>
          <h3
            style={{
              fontFamily: 'var(--gw-font-h,serif)',
              fontSize: 28,
              marginBottom: 10,
            }}
          >
            Your cart is empty
          </h3>
          <p style={{ color: '#777', marginBottom: 24 }}>
            Add rooms or items before checking out.
          </p>
          <Link to="/rooms" className="h2-btn-primary">
            Browse Rooms
          </Link>
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    );

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle title="Checkout" breadcrumbs={[{ label: 'Checkout' }]} />

      <section style={{ padding: '60px 0' }}>
        <div className="auto-container">
          <div className="row g-4">
            {/* ── Left: Guest Details ── */}
            <div className="col-lg-7">
              <div
                style={{
                  background: '#f9f8f6',
                  border: '1px solid #e8e3dc',
                  borderRadius: 8,
                  padding: '32px',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: '#b08d57',
                    marginBottom: 6,
                  }}
                >
                  Step 1
                </div>
                <h4
                  style={{
                    fontFamily: 'var(--gw-font-h,serif)',
                    fontSize: 24,
                    color: '#111',
                    marginBottom: 22,
                  }}
                >
                  Guest Details
                </h4>
                <form
                  onSubmit={handleProceedToPayment}
                  noValidate
                  id="guestForm"
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 14,
                      marginBottom: 14,
                    }}
                  >
                    {[
                      {
                        name: 'firstName',
                        label: 'First Name *',
                        ph: 'e.g. John',
                      },
                      {
                        name: 'lastName',
                        label: 'Last Name *',
                        ph: 'e.g. Emeka',
                      },
                      { name: 'email', label: 'Email *', ph: 'your@email.com' },
                      { name: 'phone', label: 'Phone *', ph: '0807 xxx xxxx' },
                    ].map((f) => (
                      <div key={f.name}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            textTransform: 'uppercase',
                            color: '#555',
                            marginBottom: 6,
                          }}
                        >
                          {f.label}
                        </label>
                        <input
                          name={f.name}
                          value={form[f.name]}
                          onChange={handle}
                          placeholder={f.ph}
                          style={{
                            width: '100%',
                            padding: '10px 13px',
                            border: `1.5px solid ${errors[f.name] ? '#dc3545' : '#e8e3dc'}`,
                            borderRadius: 4,
                            fontSize: 14,
                            outline: 'none',
                            fontFamily: 'inherit',
                            background: '#fff',
                          }}
                        />
                        {errors[f.name] && (
                          <span
                            style={{
                              fontSize: 11,
                              color: '#dc3545',
                              marginTop: 3,
                              display: 'block',
                            }}
                          >
                            {errors[f.name]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: 'uppercase',
                        color: '#555',
                        marginBottom: 6,
                      }}
                    >
                      Special Requests / Notes
                    </label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handle}
                      rows={3}
                      placeholder="Any special arrangements or requests for your stay…"
                      style={{
                        width: '100%',
                        padding: '10px 13px',
                        border: '1.5px solid #e8e3dc',
                        borderRadius: 4,
                        fontSize: 14,
                        resize: 'vertical',
                        outline: 'none',
                        fontFamily: 'inherit',
                        background: '#fff',
                      }}
                    />
                  </div>
                </form>
              </div>

              {/* Payment info teaser */}
              <div
                style={{
                  background: '#111827',
                  borderRadius: 8,
                  padding: '20px 24px',
                  marginTop: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    background: 'rgba(176,141,87,0.15)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="fas fa-building-columns"
                    style={{ color: '#b08d57', fontSize: 18 }}
                  ></i>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.5)',
                      marginBottom: 3,
                    }}
                  >
                    Step 2 — Payment
                  </div>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
                    Bank Transfer to UBA · {BANK.name}
                  </div>
                  <div
                    style={{
                      color: 'rgba(255,255,255,0.45)',
                      fontSize: 12,
                      marginTop: 2,
                    }}
                  >
                    Account details will appear after you click "Proceed to
                    Payment"
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right: Order Summary ── */}
            <div className="col-lg-5">
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e8e3dc',
                  borderRadius: 8,
                  padding: '28px',
                  position: 'sticky',
                  top: 100,
                }}
              >
                <h4
                  style={{
                    fontFamily: 'var(--gw-font-h,serif)',
                    fontSize: 22,
                    color: '#111',
                    marginBottom: 18,
                    paddingBottom: 14,
                    borderBottom: '1px solid #e8e3dc',
                  }}
                >
                  Order Summary
                </h4>

                {/* Items */}
                <div
                  style={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    marginBottom: 16,
                  }}
                >
                  {cartItems.map((item) => (
                    <div
                      key={`${item.id}-${item.type}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        paddingBottom: 12,
                        marginBottom: 12,
                        borderBottom: '1px solid #f5f5f5',
                      }}
                    >
                      <img
                        src={item.img}
                        alt={item.title}
                        style={{
                          width: 52,
                          height: 46,
                          objectFit: 'cover',
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#111',
                            marginBottom: 2,
                          }}
                        >
                          {item.title}
                        </div>
                        <div style={{ fontSize: 11, color: '#999' }}>
                          {item.type === 'room' ? '🏨 Room' : '📦 Product'} ×{' '}
                          {item.qty}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: '#b08d57',
                          flexShrink: 0,
                        }}
                      >
                        {formatCurrency(item.price * item.qty)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  {[
                    ['Subtotal', formatCurrency(cartTotal)],
                    ['Tax (8%)', formatCurrency(tax)],
                    ['Shipping', 'Free'],
                  ].map(([k, v], i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        color: v === 'Free' ? '#25d366' : '#666',
                      }}
                    >
                      <span style={{ color: '#666' }}>{k}</span>
                      <span style={{ fontWeight: v === 'Free' ? 600 : 400 }}>
                        {v}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 700,
                    fontSize: 18,
                    color: '#111',
                    borderTop: '2px solid #e8e3dc',
                    paddingTop: 14,
                    marginBottom: 22,
                  }}
                >
                  <span>Grand Total</span>
                  <span
                    style={{
                      color: '#b08d57',
                      fontFamily: 'var(--gw-font-h,serif)',
                    }}
                  >
                    {formatCurrency(grandTotal)}
                  </span>
                </div>

                {/* CTA */}
                <button
                  form="guestForm"
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#b08d57',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 5,
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 1,
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#9a7b47')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = '#b08d57')
                  }
                >
                  <i className="fas fa-building-columns"></i>
                  Proceed to Payment — {formatCurrency(grandTotal)}
                </button>
                <p
                  style={{
                    fontSize: 11,
                    color: '#aaa',
                    textAlign: 'center',
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                  }}
                >
                  <i className="fas fa-lock" style={{ color: '#25d366' }}></i>
                  Bank Transfer — UBA · {BANK.name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Modal */}
      {showModal && (
        <BankModal
          total={grandTotal}
          onConfirmed={handlePaymentConfirmed}
          onClose={() => setShowModal(false)}
        />
      )}

      <Footer />
      <ScrollToTop />
    </div>
  );
}
